import { useEffect, useMemo, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChatTeardropDots,
  Plus,
  Lock,
  Briefcase,
  Buildings,
  MagnifyingGlass,
  User,
  UsersThree,
  WifiHigh,
  WifiSlash,
} from '@phosphor-icons/react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useAuthStore } from '@/features/auth/store';
import {
  fetchThreads,
  searchMessaging,
  type MessageThread,
} from '@/lib/messagingApi';
import {
  useWebSocketStore,
  joinThread,
  leaveThread,
  onMessage,
} from '@/lib/websocket';
import { cn } from '@/lib/utils';
import { useDebounce } from '@/lib/useDebounce';
import { NewThreadModal } from './NewThreadModal';
import { ThreadPane, threadDisplayName } from './ThreadPane';
import { MessageSearchModal } from './MessageSearchModal';

interface Props {
  /** Restrict to threads tied to this matter. Omit to show all my threads. */
  matterId?: string;
  /** Hide the "New conversation" button (used inside matter Messages tab). */
  hideNewButton?: boolean;
  /** Default kind for the New Thread modal. */
  defaultKind?: 'matter_internal' | 'chambers_internal';
  /** Filter threads by kind — omit to show all. */
  kindFilter?: 'matter_internal' | 'matter_external' | 'chambers_internal' | 'cross_chambers' | null;
  /** Override the panel height. Defaults to calc(100dvh-220px). */
  height?: string;
}

interface ThreadSection {
  id: 'direct' | 'group' | 'matter';
  label: string;
  Icon: React.ElementType;
  threads: MessageThread[];
}

export function MessagingView({ matterId, hideNewButton, defaultKind, kindFilter, height }: Props) {
  const currentUser = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlThreadId = searchParams.get('thread');
  // Initialise from ?thread= URL param so search result deep-links work.
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(urlThreadId);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery.trim(), 400);
  const isServerSearch = debouncedSearch.length >= 3;

  // Server-side full-text search when query is 3+ chars
  const { data: searchResults, isFetching: searchFetching } = useQuery({
    queryKey: ['messaging', 'search', debouncedSearch, matterId],
    queryFn: () => searchMessaging(debouncedSearch, { matterId, limit: 20 }),
    enabled: isServerSearch && !!currentUser,
    staleTime: 15_000,
  });

  // WebSocket connection state
  const wsConnected = useWebSocketStore((s) => s.connected);
  const wsConnect = useWebSocketStore((s) => s.connect);

  // Ensure WebSocket is connected
  useEffect(() => {
    if (!wsConnected && currentUser) {
      wsConnect();
    }
  }, [wsConnected, wsConnect, currentUser]);

  // Fetch threads from real API
  const {
    data: threads = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['messaging', 'threads', matterId, kindFilter],
    queryFn: () => fetchThreads({ matterId, kind: kindFilter ?? undefined }),
    enabled: !!currentUser,
    staleTime: 30_000,
    refetchInterval: 120_000,
  });

  // Subscribe to real-time message events to update thread list.
  // wsConnected in deps ensures we re-subscribe after socket connects/reconnects.
  useEffect(() => {
    const unsubscribe = onMessage(() => {
      queryClient.invalidateQueries({ queryKey: ['messaging', 'threads'] });
    });
    return unsubscribe;
  }, [queryClient, wsConnected]);

  // Join/leave thread WebSocket rooms.
  //
  // `wsConnected` MUST be in the deps. Socket.io rooms live on the SERVER process and are
  // lost the moment the socket drops — a reconnect (or a rolling deploy putting the tab on a
  // different API instance) lands in a brand-new socket that is in no thread room at all. The
  // server's thread:join handler explicitly relies on the client re-issuing the join
  // ("Catches reconnecting sockets"), but with `[selectedThreadId]` alone this effect never
  // re-ran, so an open conversation silently stopped receiving live messages until the user
  // switched threads or reloaded. Keying on the connected flag re-joins on every reconnect.
  //
  // No double-join risk: the effect re-runs only when the flag actually FLIPS, and the join is
  // guarded on `wsConnected` so it is never emitted into a disconnected socket (which
  // socket.io would silently drop rather than buffer for a room membership).
  useEffect(() => {
    if (!selectedThreadId || !wsConnected) return;

    joinThread(selectedThreadId);
    return () => {
      leaveThread(selectedThreadId);
    };
  }, [selectedThreadId, wsConnected]);

  // Sort threads by last message time
  const sortedThreads = useMemo(() => {
    return [...threads].sort((a, b) => {
      const aTime = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
      const bTime = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
      return bTime - aTime;
    });
  }, [threads]);

  // URL param highlights the thread in the sidebar but does NOT auto-open it.
  // The user decides when to open — clicking the row opens the pane.
  const highlightedThreadId = urlThreadId;

  // When a thread IS selected, keep the URL in sync so the link is shareable.
  useEffect(() => {
    if (!selectedThreadId) return;
    if (searchParams.get('thread') === selectedThreadId) return;
    const next = new URLSearchParams(searchParams);
    next.set('thread', selectedThreadId);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedThreadId]);

  // Group threads into sections
  const sections: ThreadSection[] = useMemo(() => {
    if (!currentUser) return [];
    const direct: MessageThread[] = [];
    const group: MessageThread[] = [];
    const matter: MessageThread[] = [];

    const q = searchQuery.trim().toLowerCase();
    const filter = (t: MessageThread) => {
      if (!q) return true;
      const haystack = [
        threadDisplayName(t, currentUser.id),
        ...t.participants.map((p) => `${p.user.firstName} ${p.user.lastName}`),
        t.case?.caseNumber ?? '',
        t.case?.caseName ?? '',
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    };

    for (const t of sortedThreads.filter(filter)) {
      const activeOthers = t.participants.filter(
        (p) => p.userId !== currentUser.id,
      ).length;
      if (t.kind === 'matter_internal') {
        matter.push(t);
      } else if (activeOthers === 1) {
        // Two-person chambers_internal threads are direct messages.
        direct.push(t);
      } else {
        group.push(t);
      }
    }

    const all: ThreadSection[] = [
      { id: 'direct', label: 'Direct messages', Icon: User, threads: direct },
      { id: 'group', label: 'Group chats', Icon: UsersThree, threads: group },
      { id: 'matter', label: 'Matter threads', Icon: Briefcase, threads: matter },
    ];
    return all.filter((s) => s.threads.length > 0);
  }, [sortedThreads, currentUser, searchQuery]);

  // No auto-select. The user chooses which conversation to open.

  const selectedThread = useMemo(
    () => sortedThreads.find((t) => t.id === selectedThreadId) ?? null,
    [sortedThreads, selectedThreadId],
  );

  const handleThreadCreated = useCallback((thread: MessageThread) => {
    setShowNewModal(false);
    setSelectedThreadId(thread.id);
    queryClient.invalidateQueries({ queryKey: ['messaging', 'threads'] });
  }, [queryClient]);

  if (!currentUser) {
    return (
      <Card className="p-6 text-sm text-muted-foreground">
        Loading session…
      </Card>
    );
  }

  return (
    <>
      {/* Master/detail below `md`, two columns from `md` up. The fixed 260px sidebar used to
          apply at every width, so on a phone the thread pane was squeezed into whatever was
          left — about 115px — and both columns were cut off. */}
      {/* `min-h-[520px]` is a DESKTOP floor. On a phone the pane asks for 384px, the floor
          forced 520px, and the page grew to 1220px against a 664px screen — so tapping the
          message box made the browser scroll to reveal it and the composer ended up adrift with
          the rest of the page beneath it. No floor below `sm`; it still applies above.

          `dvh` rather than `vh`: on a phone `100vh` is the height the screen would have with the
          browser's address bar hidden, which is not the height you can actually see. `dvh`
          tracks the visible area. */}
      <div
        className="grid min-h-0 grid-cols-1 gap-3 sm:min-h-[520px] md:grid-cols-[260px_1fr]"
        style={{ height: height ?? 'calc(100dvh - 220px)' }}
      >
        {/* Sidebar */}
        <Card className={cn('flex flex-col overflow-hidden', selectedThreadId && 'hidden md:flex')}>
          <div className="flex items-center justify-between gap-1 border-b border-border px-3 py-3">
            <div className="flex min-w-0 items-center gap-1.5">
              <ChatTeardropDots size={18} className="shrink-0 text-brand-600 dark:text-brand-400" weight="duotone" />
              <h2 className="truncate text-sm font-semibold tracking-tight">Conversations</h2>
              <span className="shrink-0 text-xs text-muted-foreground">
                ({sortedThreads.length})
              </span>
              {/* WebSocket status indicator */}
              <span className="shrink-0" title={wsConnected ? 'Connected' : 'Reconnecting...'}>
                {wsConnected ? (
                  <WifiHigh size={12} className="text-emerald-500" weight="fill" />
                ) : (
                  <WifiSlash size={12} className="text-amber-500" weight="fill" />
                )}
              </span>
            </div>
            {/* The two actions are ONE group. As separate children of a `justify-between` row
                they were three items, so the spare width was shared out BETWEEN them and a gap
                opened up between "New" and the search icon. Grouped, the space falls where it
                should — after the title. */}
            <div className="flex shrink-0 items-center gap-1">
              {!hideNewButton && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowNewModal(true)}
                  className="h-7 shrink-0 gap-1 px-2 text-xs"
                >
                  <Plus size={14} weight="bold" />
                  New
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowSearchModal(true)}
                className="h-7 w-7 shrink-0 p-0"
                title="Search messages (Ctrl+K)"
              >
                <MagnifyingGlass size={14} />
              </Button>
            </div>
          </div>

          <div className="border-b border-border px-3 py-2">
            <div className="relative">
              <MagnifyingGlass
                size={14}
                className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search conversations…"
                className="h-8 pl-8 text-xs"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {/* Server search results */}
            {isServerSearch && (
              <div>
                {searchFetching && (
                  <div className="px-4 py-3 text-xs text-muted-foreground">Searching…</div>
                )}
                {!searchFetching && searchResults && (
                  <>
                    {searchResults.threads.length === 0 && searchResults.messages.length === 0 && (
                      <div className="px-4 py-8 text-center text-xs text-muted-foreground">
                        No results for "{debouncedSearch}"
                      </div>
                    )}
                    {searchResults.threads.length > 0 && (
                      <div>
                        <div className="sticky top-0 bg-card/95 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                          Matching threads ({searchResults.threads.length})
                        </div>
                        {searchResults.threads.map((t) => (
                          <ThreadRow
                            key={t.id}
                            t={t}
                            isSelected={t.id === selectedThreadId}
                            isHighlighted={false}
                            currentUserId={currentUser.id}
                            onSelect={() => setSelectedThreadId(t.id)}
                          />
                        ))}
                      </div>
                    )}
                    {searchResults.messages.length > 0 && (
                      <div>
                        <div className="sticky top-0 bg-card/95 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                          Messages ({searchResults.messages.length})
                        </div>
                        {searchResults.messages.map((msg) => (
                          <button
                            key={msg.id}
                            type="button"
                            onClick={() => setSelectedThreadId(msg.threadId)}
                            className="flex w-full flex-col gap-0.5 border-b border-border/60 px-4 py-2.5 text-left hover:bg-accent/40"
                          >
                            <span className="text-xs font-medium text-foreground line-clamp-2">
                              {msg.body}
                            </span>
                            <span className="text-[10px] text-muted-foreground">
                              {msg.senderUser ? `${msg.senderUser.firstName} ${msg.senderUser.lastName}` : 'Unknown'}
                              {' · '}
                              {new Date(msg.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {!isServerSearch && isLoading && (
              <div className="space-y-1">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex items-start gap-3 border-b border-border/60 px-4 py-3 animate-pulse">
                    <div className="h-9 w-9 shrink-0 rounded-full bg-muted" />
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <div className="h-4 w-32 rounded bg-muted" />
                        <div className="h-3 w-10 rounded bg-muted" />
                      </div>
                      <div className="h-3 w-48 rounded bg-muted" />
                      <div className="h-3 w-20 rounded bg-muted" />
                    </div>
                  </div>
                ))}
              </div>
            )}
            {!isServerSearch && error && (
              <div className="p-6 text-center text-sm text-red-600 dark:text-red-400">
                Failed to load conversations
              </div>
            )}
            {!isServerSearch && !isLoading && !error && sortedThreads.length === 0 && !searchQuery && (
              <div className="px-6 py-12 text-center">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-brand-50">
                  <ChatTeardropDots size={24} weight="duotone" className="text-brand-600 dark:text-brand-400" />
                </div>
                <p className="text-sm font-medium text-foreground">No conversations yet</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {matterId
                    ? 'Start a discussion about this matter with your team.'
                    : 'Connect with colleagues, clerks, or solicitors.'}
                </p>
                {!hideNewButton && (
                  <div className="mt-4">
                    <Button size="sm" variant="primary" onClick={() => setShowNewModal(true)}>
                      Start a conversation
                    </Button>
                  </div>
                )}
              </div>
            )}
            {!isServerSearch && sections.map((section) => (
              <ThreadSectionComponent
                key={section.id}
                section={section}
                selectedThreadId={selectedThreadId}
                highlightedThreadId={highlightedThreadId}
                currentUserId={currentUser.id}
                onSelect={setSelectedThreadId}
              />
            ))}
            {!isServerSearch && sections.length === 0 && sortedThreads.length > 0 && searchQuery && (
              <div className="px-6 py-10 text-center">
                <MagnifyingGlass size={20} className="mx-auto mb-2 text-muted-foreground/50" />
                <p className="text-sm text-muted-foreground">
                  No conversations match "<span className="font-medium text-foreground">{searchQuery}</span>"
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Try searching by name, matter, or case number.
                </p>
              </div>
            )}
          </div>
        </Card>

        {/* Right pane.

            On a phone an open conversation becomes its OWN SCREEN rather than a panel inside a
            page that scrolls behind it. Embedded, the matter page sat at ~1080px against a 664px
            screen, so tapping the message box made the browser scroll to reveal it and the
            composer ended up adrift with blank page beneath. Fixed to the visible viewport, the
            message list scrolls INSIDE the pane and the composer stays on the bottom edge where
            the keyboard expects it. Unchanged from `md` up, where it is a column beside the list. */}
        {selectedThread ? (
          <div className="fixed inset-0 z-50 flex h-[100dvh] flex-col bg-background [&>*]:min-h-0 [&>*]:flex-1 md:static md:z-auto md:h-auto md:bg-transparent md:[&>*]:flex-none">
          <ThreadPane
            key={selectedThread.id}
            thread={selectedThread}
            onBack={() => setSelectedThreadId(null)}
            onLeftThread={() => {
              setSelectedThreadId(null);
              queryClient.invalidateQueries({ queryKey: ['messaging', 'threads'] });
            }}
          />
          </div>
        ) : selectedThreadId && isLoading ? (
          <Card className="flex flex-1 items-center justify-center text-center">
            <div className="space-y-2">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
              <p className="text-sm text-muted-foreground">Loading conversation…</p>
            </div>
          </Card>
        ) : selectedThreadId && !isLoading && !selectedThread ? (
          <Card className="flex flex-1 items-center justify-center text-center">
            <div>
              <ChatTeardropDots size={40} weight="duotone" className="mx-auto mb-2 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">Conversation not found</p>
              <button
                type="button"
                onClick={() => setSelectedThreadId(null)}
                className="mt-2 text-xs text-brand-600 dark:text-brand-400 hover:underline"
              >
                Clear selection
              </button>
            </div>
          </Card>
        ) : (
          <Card className="hidden flex-1 items-center justify-center text-center text-sm text-muted-foreground md:flex">
            <div>
              <ChatTeardropDots size={40} weight="duotone" className="mx-auto mb-2 text-muted-foreground/40" />
              Select a conversation
            </div>
          </Card>
        )}
      </div>

      {showNewModal && (
        <NewThreadModal
          matterId={matterId}
          defaultKind={defaultKind ?? (matterId ? 'matter_internal' : 'chambers_internal')}
          onClose={() => setShowNewModal(false)}
          onCreated={handleThreadCreated}
        />
      )}

      {showSearchModal && (
        <MessageSearchModal
          matterId={matterId}
          onClose={() => setShowSearchModal(false)}
          onSelectThread={(threadId) => {
            setSelectedThreadId(threadId);
            setShowSearchModal(false);
          }}
          onSelectMessage={(threadId, _messageId) => {
            // For now, just open the thread - future: scroll to specific message
            setSelectedThreadId(threadId);
            setShowSearchModal(false);
          }}
        />
      )}
    </>
  );
}

// ─── Sidebar section + row ──────────────────────────────────────

function ThreadSectionComponent({
  section,
  selectedThreadId,
  highlightedThreadId,
  currentUserId,
  onSelect,
}: {
  section: ThreadSection;
  selectedThreadId: string | null;
  highlightedThreadId: string | null;
  currentUserId: string;
  onSelect: (id: string) => void;
}) {
  return (
    <div>
      <div className="sticky top-0 z-10 flex items-center gap-2 bg-card/95 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground backdrop-blur">
        <section.Icon size={11} weight="duotone" />
        {section.label}
        <span className="ml-auto text-[10px] font-normal text-muted-foreground/70">
          {section.threads.length}
        </span>
      </div>
      {section.threads.map((t) => (
        <ThreadRow
          key={t.id}
          t={t}
          isSelected={t.id === selectedThreadId}
          isHighlighted={t.id === highlightedThreadId}
          currentUserId={currentUserId}
          onSelect={() => onSelect(t.id)}
        />
      ))}
    </div>
  );
}

function ThreadRow({
  t,
  isSelected,
  isHighlighted,
  currentUserId,
  onSelect,
}: {
  t: MessageThread;
  isSelected: boolean;
  isHighlighted: boolean;
  currentUserId: string;
  onSelect: () => void;
}) {
  const unreadCount = t.unreadCount ?? 0;
  const isUnread = unreadCount > 0;

  const Icon =
    t.kind === 'matter_internal'
      ? Briefcase
      : t.participants.length === 2
      ? User
      : Buildings;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex w-full items-start gap-2.5 border-b border-border/60 px-3 py-2.5 text-left transition-colors hover:bg-accent/40',
        isSelected && 'bg-brand-50/70',
        // Highlighted from notification link — visible cue without auto-opening
        isHighlighted && !isSelected && 'bg-amber-50/40 dark:bg-amber-500/10 border-l-2 border-l-amber-400',
      )}
    >
      <div
        className={cn(
          'relative mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold uppercase',
          isSelected
            ? 'bg-brand-600 text-white'
            : t.kind === 'matter_internal'
            ? 'bg-brand-100 text-brand-700 dark:text-brand-300'
            : isUnread
            ? 'bg-brand-50 text-brand-700 dark:text-brand-300'
            : 'bg-muted text-muted-foreground',
        )}
      >
        <Icon size={14} weight="duotone" />
        {isUnread && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-brand-600 px-1 text-[10px] font-bold leading-none text-white shadow-sm">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <div
            className={cn(
              'truncate text-sm',
              // Unread: bold + dark; read: regular weight + muted
              isUnread ? 'font-bold text-foreground' : 'font-normal text-muted-foreground',
            )}
          >
            {threadDisplayName(t, currentUserId)}
          </div>
          {t.lastMessageAt && (
            <span className={cn(
              'shrink-0 text-xs',
              isUnread ? 'font-semibold text-brand-600 dark:text-brand-400' : 'text-muted-foreground'
            )}>
              {formatRelative(t.lastMessageAt)}
            </span>
          )}
        </div>
        {/* Last message preview */}
        {t.messages && t.messages[0] && t.messages[0].body && (
          <div className={cn(
            'mt-0.5 truncate text-xs',
            isUnread ? 'font-medium text-foreground' : 'text-muted-foreground'
          )}>
            <span className="font-medium">
              {t.messages[0].senderUserId === currentUserId
                ? 'You'
                : t.messages[0].senderUser?.firstName ?? 'Unknown'}
              :
            </span>{' '}
            <span className="opacity-80">
              {t.messages[0].body.length > 50
                ? t.messages[0].body.slice(0, 50) + '…'
                : t.messages[0].body}
            </span>
          </div>
        )}
        <div className="mt-0.5 flex items-center gap-2 text-[10px] text-muted-foreground">
          {t.case ? (
            <span className="truncate">
              {t.case.caseNumber}
            </span>
          ) : (
            <span>
              {t.participants.length} {t.participants.length === 1 ? 'person' : 'people'}
            </span>
          )}
          {t.privilege !== 'none' && (
            <Badge variant="outline" className="h-4 gap-0.5 px-1 text-[9px]">
              <Lock size={9} weight="fill" />
              {t.privilege === 'legal_advice' ? 'LAP' : 'WP'}
            </Badge>
          )}
        </div>
      </div>
    </button>
  );
}

function formatRelative(iso: string): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  if (diff < 60_000) return 'now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h`;
  if (diff < 7 * 86_400_000) return `${Math.floor(diff / 86_400_000)}d`;
  return d.toLocaleDateString([], { day: '2-digit', month: 'short' });
}
